import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualappLogo from './CualappLogo';

/**
 * Pagina publica de establecimiento de contrasena por token de email.
 * Lee ?token=XXX de la URL, valida contra /api/auth/reset-email/validate,
 * y permite establecer contrasena via /confirm. Uso unico.
 */
const SetPasswordPage = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const [validation, setValidation] = useState({ state: 'checking', email: null, error: null });
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setValidation({ state: 'invalid', email: null, error: 'Enlace incompleto.' });
      return;
    }
    axios.get(`${API}/auth/reset-email/validate`, { params: { token } })
      .then(r => setValidation({ state: 'valid', email: r.data.email, error: null }))
      .catch(err => setValidation({ state: 'invalid', email: null, error: err.response?.data?.detail || 'Enlace invalido.' }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError('La contrasena debe tener al menos 8 caracteres.'); return; }
    if (pwd !== confirm) { setError('Las contrasenas no coinciden.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-email/confirm`, { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'No fue posible establecer la contrasena.');
    } finally {
      setSubmitting(false);
    }
  };

  if (validation.state === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
        <p className="text-gray-500 text-sm" data-testid="set-password-checking">Validando enlace...</p>
      </div>
    );
  }

  if (validation.state === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="set-password-invalid">
          <div className="flex justify-center mb-4"><CualappLogo size={72} animated={true} /></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Enlace no valido</h1>
          <p className="text-gray-600 text-sm mb-6">{validation.error}</p>
          <a href="/" className="inline-block px-5 py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium">Ir al inicio</a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="set-password-done">
          <div className="flex justify-center mb-4"><CualappLogo size={72} animated={true} /></div>
          <h1 className="text-2xl font-bold text-emerald-700 mb-2">Contrasena establecida</h1>
          <p className="text-gray-600 text-sm mb-6">Ya puedes iniciar sesion con tu nueva contrasena.</p>
          <a href="/login" data-testid="set-password-goto-login" className="inline-block px-5 py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium">Iniciar sesion</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8" data-testid="set-password-form-panel">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3"><CualappLogo size={72} animated={true} /></div>
          <h1 className="text-2xl font-bold text-gray-900">Establecer contrasena</h1>
          <p className="text-sm text-gray-500 mt-1">Para <strong>{validation.email}</strong></p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contrasena</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8}
                data-testid="set-password-input"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-500 hover:text-gray-700">
                {showPwd ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Minimo 8 caracteres.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contrasena</label>
            <input type={showPwd ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
              data-testid="set-password-confirm-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" data-testid="set-password-error">{error}</div>}
          <button type="submit" disabled={submitting}
            data-testid="set-password-submit"
            className="w-full py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium disabled:opacity-50">
            {submitting ? 'Estableciendo...' : 'Establecer contrasena'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetPasswordPage;
